import {
  Sidebar,
  Expand,
  Template,
  Box,
  Cover,
  Stack,
  Cluster,
  Icon,
  Heading,
  Divider,
  Label,
  Checkbox,
  FormItem,
  Select,
  ISelectOption,
  Button,
  Spinner,
  useValidateForm,
  Toast,
} from '@pluto-tv/assemble';
import {useUserRegions} from 'helpers/useUserRegions';
import {orderBy} from 'lodash-es';
import {IVodCollection} from 'models/vodCollections';
import React from 'react';
import {vodCollectionSearchValidator} from 'views/programming/mainCategories/validators';
import {useFindQuery as useFindDevicesQuery} from 'features/devices/devicesApi';
import useToggleSearchBarOnSlash from 'helpers/useToggleSearchBarOnSlash';
import ReorderVodCollectionList from 'views/programming/mainCategories/edit/vodCollectionList/ReorderVodCollectionList';
import VodCollectionTable from 'views/programming/mainCategories/edit/vodCollectionList/VodCollectionTable';
import {useAppPermissions} from 'app/permissions';
import {useVodCollectionSearchlLazyLoadProvider} from 'views/programming/mainCategories/edit/vodCollectionList/providers/VodCollectionSearchLazyProvider';
import {useBulkReorderMutation} from 'features/mainCategories/mainCategoriesApi';
import {withThousandsSeparator} from 'utils/thousands-separator';

import {useParams} from 'react-router-dom';
import {getFilteredDeviceExcludedList, getFilteredDeviceIncludedList} from 'utils/deviceUtils';
import {IMainCategoryVODCollectionSearch} from 'models/mainCategories';

export const INIT_PARAMS = {
  enabled: true,
  limit: 250,
};

const VodCollectionList = (): JSX.Element => {
  const {ableTo} = useAppPermissions();
  const {id} = useParams<{id: string}>();

  const {
    items: vodCollectionList,
    lazyLoad,
    isLazyLoading,
    isError,
    error,
    isLoading,
    search: searchVodCollection,
  } = useVodCollectionSearchlLazyLoadProvider();

  const errorNotFound = error && 'status' in error && error.status === 404;

  const [searchExpanded, setSearchExpanded] = React.useState(true);
  const [reorderOpen, setReorderOpen] = React.useState(false);
  const [isSearchActive, setIsSearchActive] = React.useState(false);
  const [updateVodOrderBulk] = useBulkReorderMutation();
  const [searchParams, setSearchParams] = React.useState<Partial<IMainCategoryVODCollectionSearch>>({});

  const {territories, isFetching: isTerritoriesFetching} = useUserRegions();
  const {data: deviceTypesList, isFetching: isDevicesTypesFetching} = useFindDevicesQuery({
    offset: 0,
    limit: 250,
    sort: 'name:asc',
  });

  useToggleSearchBarOnSlash(setSearchExpanded, searchExpanded);

  const {
    model: searchModel,
    setFields: setSearchFields,
    form: searchForm,
    resetToPristine: resetSearchToPristine,
    setModel: setSearchModel,
    getValidation,
  } = useValidateForm<IVodCollection>(vodCollectionSearchValidator, 'ask');

  const filteredDeviceIncludedList = React.useMemo(() => {
    return getFilteredDeviceIncludedList(deviceTypesList?.data || [], searchModel.devicesExcluded || []);
  }, [deviceTypesList?.data, searchModel.devicesExcluded]);

  const filteredDeviceExcludedList = React.useMemo(() => {
    return getFilteredDeviceExcludedList(deviceTypesList?.data || [], searchModel.devicesIncluded || []);
  }, [deviceTypesList?.data, searchModel.devicesIncluded]);

  React.useEffect(() => {
    setSearchModel({enabled: true});
  }, [setSearchModel]);

  const getEnabled = (enabled: boolean, unpublished: boolean) => {
    if (enabled && unpublished) return {};
    if (enabled) return {enabled: true};
    if (unpublished) return {enabled: false};
    return {};
  };

  const getParams = React.useCallback(model => {
    const {enabled, unpublished, territoriesIncluded, ...rest} = model;

    return {
      ...rest,
      ...getEnabled(enabled || false, unpublished || false),
      ...((territoriesIncluded || []).length ? {regionFilter: territoriesIncluded} : {}),
    };
  }, []);

  React.useEffect(() => {
    setSearchParams(getParams(searchModel));
  }, [searchModel, getParams]);

  const handleSearch = async () => {
    setIsSearchActive(true);
    const {model} = await getValidation();

    if (Object.keys(model).length > 0) {
      const params = getParams(model);

      searchVodCollection({...params, id});
    }
  };

  const handleClear = async () => {
    if (isSearchActive) {
      setIsSearchActive(false);
    }
    resetSearchToPristine();
    searchVodCollection({...INIT_PARAMS, id});
  };

  React.useEffect(() => {
    searchVodCollection({...INIT_PARAMS, id});
  }, [id, searchVodCollection]);

  const handleReorderSave = async (reorderedRows: IVodCollection[]) => {
    const updatedOrders = reorderedRows.map(row => row.id);

    await handleOrderUpdate(updatedOrders);
    setReorderOpen(false);
    searchVodCollection({...searchParams, id});
  };

  const handleReorderCancel = () => {
    setReorderOpen(false);
    searchVodCollection({...searchParams, id});
  };

  const handleOrderUpdate = React.useCallback(
    async (vodOrderCollection: string[]) => {
      try {
        const result: any = await updateVodOrderBulk({
          id: id,
          vodIds: vodOrderCollection,
        });

        if ('error' in result) {
          Toast.error('error', `${result.error.data.message}.`);
          return;
        }

        Toast.success('Successfully reordered VOD Collection');

        return result;
      } catch (e) {
        Toast.error('Error', 'Failed to reorder VOD Collection. Please try again');
      }
    },
    [updateVodOrderBulk, id],
  );

  if (isTerritoriesFetching || isDevicesTypesFetching) {
    return (
      <Box fullHeight={true}>
        <Spinner center={true} minHeight='9.375rem' size='xlarge' />
      </Box>
    );
  }

  return (
    <Sidebar fullHeight={true}>
      <Expand width='21rem' height='100%' fullHeightContainer={true} isExpanded={searchExpanded}>
        <Template label='expandable'>
          <Box
            background='pewter'
            paddingY={'medium'}
            paddingRight='medium'
            paddingLeft={'medium'}
            fullHeight={true}
            marginRight='large'
            borderTop={true}
            borderSize='0.125rem'
            marginTop='large'
            borderColor='cavern'
          >
            <Cover scrolling={true} gutter='medium'>
              <Template label='header'>
                <Stack space='medium'>
                  <Cluster align='center' justify='space-between'>
                    <Icon icon='tune' space='small' size='large' iconAlign='center'>
                      <Heading level='h4'>Search Filters</Heading>
                    </Icon>
                    <Icon
                      icon='collapseleft'
                      id='closeFilter'
                      size='large'
                      onClick={() => setSearchExpanded(!searchExpanded)}
                    />
                  </Cluster>
                  <Divider color='graphite' />
                </Stack>
              </Template>
              <Template label='cover'>
                <form
                  onSubmit={ev => {
                    ev.preventDefault();
                    setTimeout(() => alert('Searching...'));
                  }}
                >
                  <Box paddingY='small'>
                    <Stack space='small'>
                      {/* Using this to allow pressing enter to submit form */}
                      <input type='submit' style={{display: 'none'}} />
                      <Stack space='xlarge'>
                        <Stack space='small'>
                          <Label>Status</Label>
                          <Stack space='small'>
                            <Checkbox
                              label='Published'
                              onChange={val => setSearchFields({enabled: val})}
                              value={searchModel.enabled}
                              id='publishedFilter'
                            />
                            <Checkbox
                              label='Unpublished'
                              onChange={val => setSearchFields({unpublished: val})}
                              value={searchModel.unpublished}
                              id='unPublishedFilter'
                            />
                          </Stack>
                        </Stack>
                      </Stack>
                      <Divider color='graphite' />
                      <FormItem {...searchForm.territoriesIncluded}>
                        <Select
                          clearable={true}
                          multiselect={true}
                          placeholder='Select Territory'
                          searchable={true}
                          value={(searchModel.territoriesIncluded || [])?.map(d => ({label: d, value: d}))}
                          onChange={val =>
                            setSearchFields({
                              territoriesIncluded: (val || []).map(v => v.value),
                            })
                          }
                          options={(territories || []).map(te => ({
                            label: `${te.name}`,
                            value: te.id,
                          }))}
                          predicate='value'
                          id='territoriesIncluded'
                        />
                      </FormItem>
                      <FormItem {...searchForm.devicesIncluded}>
                        <Select
                          id='devicesIncluded'
                          clearable={true}
                          placeholder='Select Devices'
                          multiselect={true}
                          appendToBody={true}
                          value={searchModel.devicesIncluded?.map(p => ({label: p, value: p}))}
                          options={filteredDeviceIncludedList}
                          onChange={value => {
                            setSearchFields({
                              devicesIncluded: ((value as ISelectOption[]) || []).map(v => v.value),
                            });
                          }}
                          predicate='value'
                          searchable={true}
                          onSearch={term =>
                            orderBy(
                              (filteredDeviceIncludedList || [])
                                .filter(p => p.label.toLowerCase().includes(term.toLowerCase()))
                                .map(p => ({label: p.label, value: p.value}), 'label'),
                            ) || ([] as ISelectOption[])
                          }
                        />
                      </FormItem>
                      <FormItem {...searchForm.devicesExcluded}>
                        <Select
                          id='devicesExcluded'
                          clearable={true}
                          placeholder='Select Devices'
                          multiselect={true}
                          appendToBody={true}
                          value={searchModel.devicesExcluded?.map(p => ({label: p, value: p}))}
                          options={filteredDeviceExcludedList}
                          onChange={value => {
                            setSearchFields({
                              devicesExcluded: ((value as ISelectOption[]) || []).map(v => v.value),
                            });
                          }}
                          predicate='value'
                          searchable={true}
                          onSearch={term =>
                            orderBy(
                              (filteredDeviceExcludedList || [])
                                .filter(p => p.label.toLowerCase().includes(term.toLowerCase()))
                                .map(p => ({label: p.label, value: p.value}), 'label'),
                            ) || ([] as ISelectOption[])
                          }
                        />
                      </FormItem>
                      <FormItem {...searchForm.kidsMode}>
                        <Select
                          id='kids'
                          clearable={true}
                          placeholder='Select Kids Only'
                          value={
                            searchModel.kidsMode === undefined
                              ? undefined
                              : searchModel.kidsMode
                              ? {label: 'Yes'}
                              : {label: 'No'}
                          }
                          options={[{label: 'Yes'}, {label: 'No'}]}
                          onChange={value => {
                            if (!value) {
                              setSearchFields({kidsMode: undefined});
                            } else {
                              setSearchFields({kidsMode: value.label === 'Yes'});
                            }
                          }}
                        />
                      </FormItem>
                      <FormItem {...searchForm.plutoOfficeOnly}>
                        <Select
                          id='officeOnly'
                          clearable={true}
                          placeholder='Select Office Only'
                          options={[{label: 'Yes'}, {label: 'No'}]}
                          value={
                            searchModel.plutoOfficeOnly === undefined
                              ? undefined
                              : searchModel.plutoOfficeOnly
                              ? {label: 'Yes'}
                              : {label: 'No'}
                          }
                          onChange={value => {
                            if (!value) {
                              setSearchFields({plutoOfficeOnly: undefined});
                            } else {
                              setSearchFields({plutoOfficeOnly: value.label === 'Yes'});
                            }
                          }}
                        />
                      </FormItem>
                    </Stack>
                  </Box>
                </form>
              </Template>
              <Template label='footer'>
                <Cluster justify='space-between'>
                  <div></div>
                  <Cluster space='small'>
                    <Button ghost={true} state={isLoading ? 'disabled' : ''} onClick={handleClear} id='clearButton'>
                      Clear
                    </Button>
                    <Button type='primary' state={isLoading ? 'thinking' : ''} onClick={handleSearch} id='searchButton'>
                      Search
                    </Button>
                  </Cluster>
                </Cluster>
              </Template>
            </Cover>
          </Box>
        </Template>
      </Expand>
      <Cover scrolling={true} gutterBottom='large' coverTemplateHeight='100%' overflow='auto' paddingY={'medium'}>
        <Template label='header'>
          <Box
            id='vodCollectionHeader'
            borderTop={true}
            borderSize='0.125rem'
            borderColor='cavern'
            paddingTop='small'
            paddingBottom='small'
            paddingX='large'
            marginTop='xxxxxsmall'
          >
            <Cluster justify='space-between' align='center' space='medium'>
              <Cluster align='end' space='small'>
                <Heading level='h2' color='secondary'>
                  VOD Collections
                </Heading>
                <Cluster space='small' align='center'>
                  <Icon
                    icon='tune'
                    space='xxxsmall'
                    verticalAlign='bottom'
                    lineHeight='0px'
                    onClick={() => setSearchExpanded(!searchExpanded)}
                    id='expandFilter'
                  >
                    {withThousandsSeparator(vodCollectionList.length)} Items
                  </Icon>
                </Cluster>
              </Cluster>
              <Cluster space='small' align='center'>
                <>
                  <Button
                    id='reorderBtn'
                    onClick={() => setReorderOpen(true)}
                    state={vodCollectionList.filter(c => c.enabled).length === 0 ? 'disabled' : ''}
                    permission={ableTo('MAIN_CATEGORIES_EDIT') ? '' : 'disabled'}
                  >
                    Reorder
                  </Button>

                  {reorderOpen && (
                    <ReorderVodCollectionList
                      setReorderOpen={setReorderOpen}
                      handleReorderSave={handleReorderSave}
                      handleReorderCancel={handleReorderCancel}
                      reorderOpen={reorderOpen}
                      categoryId={id}
                    />
                  )}
                </>
              </Cluster>
            </Cluster>
          </Box>
        </Template>
        <Template label='cover'>
          <VodCollectionTable
            items={vodCollectionList || []}
            isLoading={isLoading}
            isLazyLoading={isLazyLoading}
            lazyLoad={lazyLoad}
            isError={isError && !errorNotFound}
            id='vodCollectionList'
          />
        </Template>
      </Cover>
    </Sidebar>
  );
};

VodCollectionList.displayName = 'VodCollectionList';
export default React.memo(VodCollectionList);
